import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import Testimonials from '../content/Testimonials';
import PricingFaq from '../faqs/PricingFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import CDN_BASE, { cdnPhoto } from '../../../utils/cdn';

function CateringPrices() {
  const { vehicle_type, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();
  const citySlug = city_name.toLowerCase();
  const { content: cms } = usePageContent('catering_pricing');

  return (
    <div className='CateringPage'>
      <Meta
        title={cms?.meta?.title || `Food ${vehicle_type} Catering Prices & Cost`}
        type='website'
        canonical='/food-truck-catering/prices'
        image={cdnPhoto(`${citySlug}-food-truck-catering`)}
        description={cms?.meta?.description || `How much does food ${vehicleType} catering cost? See what drives the price and compare free quotes from ${city_name} food ${vehicleType}s.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Catering Prices in ${city_name}`,
            description: `Transparent food ${vehicleType} catering pricing in ${city_name}. Compare quotes, understand costs, and book the best ${vehicleType} for your budget.`,
            url: '/food-truck-catering/prices',
            serviceType: 'Food Truck Catering Pricing',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Catering Prices', item: '/food-truck-catering/prices' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-food-truck-catering.webp`} alt='Food truck catering pricing overview' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>{cms?.hero?.headline || `How much does food ${vehicleType} catering cost?`}</h1>
            <CmsRichText
              html={cms?.hero?.subheadline}
              fallback={
                <p>Pricing depends on guest count, menu, service style, event length, and location. Use the estimator below for a ballpark, then send your event details to compare actual quotes from available {vehicleType}s.</p>
              }
            />
            <p>
              <Link to='/catering/requests/new' className='Button Button--mobile-w100'>{cms?.hero?.cta_label || 'Get catering quotes'}</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <CostEstimator />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>What goes into a food {vehicleType} catering quote</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-food-truck-catering-event.webp`} alt='Food truck serving guests at a catered event' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>How {vehicleType}s price catering</h3>
            <p>Most quotes are per person for the food, and many {vehicleType}s set a minimum for the booking. Small groups often land at the minimum, so the per-person cost tends to fall as the guest count rises. The menu matters too: a taco menu and a seafood menu won&#39;t quote the same.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-food-truck-catering-service.webp`} alt='Factors affecting food truck catering cost' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>What moves the price</h3>
            <p>Guest count, menu, and service style, meaning whether guests order at the window or the {vehicleType} sets up a buffet line. Then event length, travel distance, and add-ons such as drinks, dessert, or dietary options. Give {vehicleType}s the full picture in your request, and the quotes will be closer to the final number.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-food-truck-catering-setup.webp`} alt='Food truck catering setup with full service' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>What&#39;s usually included</h3>
            <p>The food, the crew, service during your event, setup, and cleanup. Plates and napkins are often included; drinks, dessert, or extra service hours may be add-ons. Check each quote for what&#39;s included, and ask the {vehicleType} if something isn&#39;t clear. See the full price, including fees, before you book.</p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <Testimonials title='What customers say' />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <PricingFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta title='Ready to compare quotes?' />
      </section>
    </div>
  );
}

export default CateringPrices;
